import React, { memo, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrag, useDrop } from 'react-dnd'
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    Grid, MenuItem,
    Tab,
    Table,
    Tabs,
    TextField,
} from '@mui/material';
import ViewPDFDialog from 'components/dialogs/ViewPDFDialog';
import { a11yProps, TabPanel } from 'components/mui/Tabs';
import { Draggables } from 'config/draggable';
import { ModalActionProps } from 'models/AutoModals';
import { CarModel } from 'models/StockModel';
import { arrayGrouped } from 'utils/common';


const batchNumbers = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20];

type JpInvoiceSeparatorDialogProps = {
    urls: string[],
    cars: CarModel[],
} & ModalActionProps

type SimpleCar = {
    batch: number,
    pos: number,
    id: number,
    chassisNumber: string,
}

const JpInvoiceSeparatorDialog = ({urls, cars: init, onFinish}: JpInvoiceSeparatorDialogProps) => {
    const {t} = useTranslation();
    const [tab, setTab] = useState(0);
    const [cars, setCars] = useState<Record<number, SimpleCar[]>>(() => {
        const sorted: SimpleCar[] = [...init]
            .sort((a, b) => (a.jpInvoice?.pos || 0) - (b.jpInvoice?.pos || 0) || a.id - b.id)
            .map(car => ({
                batch: car.jpInvoice?.batch || 1, // for grouping only
                pos: 0, // for grouping only
                id: car.id,
                chassisNumber: car.chassisNumber,
            }));

        return arrayGrouped(sorted, 'batch');
    });

    const changeTab = (_: React.SyntheticEvent, newValue: number) => setTab(newValue);

    const changeBatch = useCallback((from: number, to: number, id: number) => {
        setCars(old => {
            const oldBatch = [...old[from]];
            const newBatch = old[to] ? [...old[to]] : [];

            const idx = oldBatch.findIndex(car => car.id === id);
            newBatch.push(oldBatch.splice(idx, 1)[0]);

            return {
                ...old,
                [from]: oldBatch,
                [to]: newBatch,
            }
        });
    }, []);

    const changePos = useCallback((dragged: SimpleCar, hovered: SimpleCar): [number, number] => {
        if (dragged.batch === hovered.batch) {
            setCars(old => {
                let temp = [...old[dragged.batch]];
                const item = temp[dragged.pos];
                temp[dragged.pos] = temp[hovered.pos];
                temp[hovered.pos] = item;

                return {
                    ...old,
                    [dragged.batch]: temp,
                }
            });

            return [hovered.batch, hovered.pos];
        }

        let itemCount = 0;

        setCars(old => {
            const oldBatch = [...old[dragged.batch]];
            const newBatch = [...old[hovered.batch]];
            itemCount = old[hovered.batch].length;

            if (dragged.batch > hovered.batch) { // to prev batch
                newBatch.push(oldBatch.shift()!);
            } else { // to next batch
                newBatch.unshift(oldBatch.pop()!);
            }

            return {
                ...old,
                [dragged.batch]: oldBatch,
                [hovered.batch]: newBatch,
            }
        });

        return [
            hovered.batch,
            dragged.batch > hovered.batch ? itemCount : 0
        ];
    }, []);

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = Object.fromEntries(new FormData(event.currentTarget).entries());

        onFinish(data);
    };

    const id = 'eds';

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={9} sx={{mt: {xs: 2}}}>
                        {urls.length === 1 && (
                            <ViewPDFDialog url={urls[0]}/>
                        )}

                        {urls.length > 1 && (
                            <Box>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs
                                        value={tab}
                                        onChange={changeTab}
                                        aria-label="export declarations"
                                    >
                                        {urls.map((_, i) => (
                                            <Tab key={i} label={`ED ${i+1}`} {...a11yProps(id, i)} />
                                        ))}
                                    </Tabs>
                                </Box>

                                {urls.map((url, i) => (
                                    <TabPanel
                                        key={i}
                                        open={tab === i}
                                        id={id}
                                        index={i}
                                        keepMounted
                                        narrow
                                    >
                                        <ViewPDFDialog url={url}/>
                                    </TabPanel>
                                ))}
                            </Box>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Box sx={{overflow: 'auto', height: '720px'}}>
                            {Object.entries(cars).map(([batch, items]) => (
                                <BatchTable
                                    key={batch}
                                    batch={batch}
                                    items={items}
                                    changeBatch={changeBatch}
                                    changePos={changePos}
                                />
                            ))}

                            <DropTarget
                                onFinish={changeBatch}
                                batch={Object.keys(cars).length + 1}
                            >
                                {t('new_batch')}
                            </DropTarget>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button type="submit">{t('apply')}</Button>
            </DialogActions>
        </Box>
    );
};


type BatchTableProps = {
    batch: string,
    items: SimpleCar[],
    changeBatch: (from: number, to: number, id: number) => void,
    changePos: (dragged: SimpleCar, hovered: SimpleCar) => [number, number],
}

const BatchTable = memo(({batch, items, changeBatch, changePos}: BatchTableProps) => {
    const {t} = useTranslation();

    return (
        <Table
            key={batch}
            sx={{'tr:nth-of-type(10n)': {borderBottom: '1px solid black'}}}
        >
            <thead>
            <tr>
                <th style={{width: '15%'}}>{t('batch')}</th>
                <th style={{width: '15%'}}>{t('pos')}</th>
                <th>{t('chassis')}</th>
            </tr>
            </thead>
            <tbody>
            {items.map((car, pos) => (
                <Row
                    key={car.id}
                    batch={batch as unknown as number}
                    pos={pos}
                    car={car}
                    changeBatch={changeBatch}
                    changePos={changePos}
                />
            ))}
            </tbody>
        </Table>
    )
});


type DropTargetProps = PropsWithChildren<{
    onFinish: (from: number, to: number, id: number) => void,
    batch: number,
}>

const DropTarget = ({children, onFinish, batch}: DropTargetProps) => {
    const [{ isOver }, dropRef] = useDrop({
        accept: Draggables.ROW,
        drop: (item: SimpleCar) => {
            onFinish(item.batch, batch, item.id)
        },
        collect: (monitor) => ({
            isOver: monitor.isOver()
        })
    });

    return (
        <Box
            ref={dropRef}
            textAlign="center"
            padding="10px"
            bgcolor={isOver ? 'success.light' : 'grey.50'}
        >
            {children}
        </Box>
    )
};

type RowProps = {
    batch: number,
    pos: number,
    car: SimpleCar,
    changeBatch: (from: number, to: number, id: number) => void,
    changePos: (dragged: SimpleCar, hovered: SimpleCar) => [number, number],
}

const Row = ({batch, pos, car, changeBatch, changePos}: RowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);
    const [{ opacity }, dragRef] = useDrag({
        type: Draggables.ROW,
        item: {
            ...car,
            batch,
            pos,
        },
        collect: (monitor) => ({
            opacity: monitor.isDragging() ? 0.2 : 1
        }),
    });

    const [, dropRef] = useDrop({
        accept: Draggables.ROW,
        hover: (item: SimpleCar, monitor) => {
            if (!ref.current) return;

            const draggedIndex = item.pos;
            const hoveredIndex = pos;

            if (draggedIndex === hoveredIndex && item.batch === batch)
                return

            const hoveredRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoveredRect.bottom - hoveredRect.top) / 2;
            const mousePosition = monitor.getClientOffset()!;
            const hoverClientY = mousePosition.y - hoveredRect.top;

            if (draggedIndex < hoveredIndex && hoverClientY < hoverMiddleY)
                return;

            if (draggedIndex > hoveredIndex && hoverClientY > hoverMiddleY)
                return;

            const [newBatch, newPos] = changePos({...item}, {...car, batch, pos});
            item.batch = newBatch;
            item.pos = newPos;
        },
    });

    dragRef(dropRef(ref));

    return (
        <tr ref={ref} style={{ opacity }}>
            <td>
                <TextField
                    id={`inv-${car.id}-batch`}
                    name={`inv[${car.id}][batch]`}
                    value={batch}
                    select
                    onChange={e => changeBatch(batch, Number(e.target.value), car.id)}
                >
                    {batchNumbers.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
            </td>
            <td>
                {pos+1}
                <input
                    type="hidden"
                    name={`inv[${car.id}][pos]`}
                    value={pos+1}
                />
            </td>
            <td style={{paddingLeft: '2rem'}}>{car.chassisNumber}</td>
        </tr>
    )
};

export default JpInvoiceSeparatorDialog;
